import "leaflet/dist/leaflet.css";
import React, { Fragment, useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import * as L from "leaflet";
import { MapContainer, Marker, ImageOverlay } from "react-leaflet";
import { fetchPoints } from "../../api/points";
import { fetchTrainAudio } from "../../api/trainAudio";
import ModalLocationDetail from "../ModalLocationDetail";
import { useGeolocationContext, useSettings } from "../../context";
import { useDebounce } from "react-use";
import { getDistanceFromLatLonInMeters } from "../../utils/geolocation";
import moment from 'moment';

const POI_DISTANCE = process.env.REACT_APP_POI_DISTANCE || 10;

const carIcon = new L.Icon({
  iconUrl: "/images/car.png",
  iconRetinaUrl: "/images/car.png",
  className: "h-12 w-12 -ml-6 -mt-10 !z-[700]",
});

const markerGreenIcon = new L.Icon({
  iconUrl: "/images/marker-green.svg",
  iconRetinaUrl: "/images/marker-green.svg",
  className: "h-12 w-12 -ml-6 -mt-12 ",
  //i margini negativi servono per allineare il marker nella posizione corretta
});

const markerYellowIcon = new L.Icon({
  iconUrl: "/images/marker-yellow.svg",
  iconRetinaUrl: "/images/marker-yellow.svg",
  className: "h-12 w-12 -ml-6 -mt-12",
});

const LeafletMap = () => {
  const { i18n } = useTranslation();
  const { state: geolocation } = useGeolocationContext();

  const [isInArea, setIsInArea] = useState(false);
  const [point, setPoint] = useState({});
  const [pointsDefault, setPointsDefault] = useState([]);
  const [points, setPoints] = useState([]);
  const [open, setOpen] = useState(false);
  const [trainAudio, setTrainAudio] = useState([]);
  const {
    activeMarkers,
    isTrainMode,
    setTrainModePlay,
    showMarkers,
    trainModePlay,
    trainModePlayManuallyStoppedRef,
  } = useSettings()
  const trainAudioRef = useRef(null);
  const openOnceRef = useRef(false);

  const bounds = L.latLngBounds([
    [45.355103, 10.72525183],
    [45.367424, 10.73716783],
  ]);

  useDebounce(
    () => {
      if (isInArea) {
        setOpen(true);
      }
    },
    100,
    [isInArea]
  );

  useEffect(() => {
    (async () => {
      const dataDefault = await fetchPoints({ isTrainMode, locale: "it" });
      const data = await fetchPoints({ isTrainMode, locale: i18n.language });
      const trainAudioData = await fetchTrainAudio();
      setPointsDefault(dataDefault);
      setPoints(data);
      setTrainAudio(trainAudioData);
    })();
  }, [i18n.language, isTrainMode]);

  useEffect(() => {
    if (!isTrainMode) return;

    if (!openOnceRef.current) {
      if (open) openOnceRef.current = true;
      else return;
    }

    if (trainModePlayManuallyStoppedRef.current) return;

    setTrainModePlay(!open);
  }, [isTrainMode, open]);

  useEffect(() => {
    if (!isTrainMode) return;

    trainModePlay
      ? trainAudioRef.current?.play()
      : trainAudioRef.current?.pause();
  }, [trainModePlay]);

  const isCurrent = useCallback(({ endDate, startDate } = {}) => {
    let end = endDate ? moment(endDate) : undefined;
    let start = startDate ? moment(startDate) : undefined;

    if (!end || !start || !end.isValid() || !start.isValid()) return false;

    const now = moment();
    end.year(now.year());
    end = end.endOf('day');
    start.year(now.year());

    return now.isSameOrAfter(start) && now.isSameOrBefore(end);
  }, []);

  const trainAudioUrl = useMemo(() => {
    if (!Array.isArray(trainAudio) || !trainAudio.length) return;

    let audioAttrs;

    for (const item of trainAudio) {
      const { attributes } = item || {};
      const { audio, endDate, startDate } = attributes || {};

      if (!isCurrent({ endDate, startDate })) continue;

      ({ attributes: audioAttrs = {} } = audio.data || {});
      break;
    }

    if (!audioAttrs) {
      const { attributes } = trainAudio[0] || {};
      const { audio } = attributes || {};
      ({ attributes: audioAttrs = {} } = audio.data || {});
    }

    const { url } = audioAttrs || {};
    return url || '';
  }, [isCurrent, trainAudio]);

  const center = bounds.getCenter();

  const allPoints = useMemo(() => {
    //Unisce i POI di default (it) con quelli presenti nelle altre lingue
    const mergedData = new Map();

    pointsDefault.concat(points).forEach(function (obj) {
      if (isTrainMode) {
        const { attributes } = obj || {};
        const { endDate, startDate } = attributes || {};
        if (!isCurrent({ endDate, startDate })) return;
      }

      mergedData.set(
        obj.attributes.latitude,
        Object.assign(mergedData.get(obj.attributes.latitude) || {}, obj),
      );
    });

    return Array.from(mergedData.values());
  }, [isCurrent, isTrainMode, points, pointsDefault]);

  useEffect(() => {
    if (geolocation.error) {
      console.log(geolocation.error.message);
      console.log(JSON.stringify(geolocation.error));
    } else if (!geolocation.loading) {
      let isInArea = false;

      for (const p of allPoints) {
        const { attributes } = p;

        const calculatedDistance = getDistanceFromLatLonInMeters(
          geolocation.latitude,
          geolocation.longitude,
          attributes.latitude,
          attributes.longitude
        );

        if (calculatedDistance <= POI_DISTANCE) {
          isInArea = true;
          setPoint(p);
          break;
        }
      }

      setIsInArea(isInArea);
    }
  }, [allPoints, geolocation]);

  return (
    <Fragment>
      <MapContainer
        id="map"
        zoom={16}
        minZoom={16.6}
        center={center}
        dragging={false}
        zoomSnap={false}
        zoomDelta={false}
        trackResize={false}
        touchZoom={false}
        scrollWheelZoom={false}
        zoomControl={false}
        doubleClickZoom={false}
        closePopupOnClick={false}
        attributionControl={false}
        style={{ height: "calc(100vh - 80px)" }}
      >
        {/* Non ci servono perchè usiamo solo l'overlay */}
        {/* <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        /> */}

        <ImageOverlay url="/images/mapoverlay.jpg" bounds={bounds} />

        {showMarkers &&
          allPoints.map((point) => {
            const { id, attributes } = point;
            const { type, latitude, longitude } = attributes;

            return (
              <Marker
                key={id}
                icon={type === "Poi" ? markerGreenIcon : markerYellowIcon}
                position={[
                  parseFloat(latitude) || 45.356,
                  parseFloat(longitude) || 10.7278,
                ]}
                eventHandlers={{
                  click: () => {
                    if (!activeMarkers) return;

                    setPoint(point);
                    setOpen(true);
                  },
                }}
              />
            );
          })}

        {!geolocation.loading && !geolocation.error && (
          <Marker
            icon={carIcon}
            position={[
              parseFloat(geolocation.latitude),
              parseFloat(geolocation.longitude),
            ]}
          />
        )}
      </MapContainer>

      <ModalLocationDetail open={open} setOpen={setOpen} point={point} />

      {!!trainAudioUrl && (
        <audio
          className="invisible"
          loop
          muted={open || !isTrainMode}
          ref={trainAudioRef}
          src={trainAudioUrl}
        />
      )}
    </Fragment>
  );
};

export default LeafletMap;
